import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Edit, Trash2, Tag, Clock, CheckCircle, XCircle, Plus, UserPlus } from 'lucide-react';
import { MarketingProgramResource, KloaMarketingProgramCouponResource, CompanyResource } from '../types';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import CompanySearchDialog from '../components/CompanySearchDialog';
import GenerateEmptyCouponsDialog from '../components/GenerateEmptyCouponsDialog';
import { useAuth } from '../contexts/AuthContext';

const MarketingProgramDetail: React.FC = () => {
  const { programId } = useParams<{ programId: string }>();
  const navigate = useNavigate();
  const { canManage } = useAuth();
  const location = useLocation();
  const [program, setProgram] = useState<MarketingProgramResource | null>(null);
  const [coupons, setCoupons] = useState<KloaMarketingProgramCouponResource[]>([]);
  const [loading, setLoading] = useState(true);
  const [couponsLoading, setCouponsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCompanySearch, setShowCompanySearch] = useState(false);
  const [showGenerateEmpty, setShowGenerateEmpty] = useState(false);

  // Load marketing program details from navigation state
  const loadProgram = () => {
    setLoading(true);
    setError(null);
    
    // Get program data from navigation state
    const programData = location.state?.program as MarketingProgramResource;
    
    if (programData) {
      setProgram(programData);
      setLoading(false);
    } else {
      // If no data in state, redirect back to list
      setError('No program data available');
      setLoading(false);
      // Optionally redirect back to list after a short delay
      setTimeout(() => {
        navigate('/marketing-programs');
      }, 2000);
    }
  };

  // Load coupons for coupon-type programs
  const loadCoupons = async () => {
    if (!programId || !program || program.type !== 'coupon') return;
    
    try {
      setCouponsLoading(true);
      const response = await api.getMarketingProgramCoupons(programId);
      setCoupons(response);
    } catch (error) {
      console.error('Error loading coupons:', error);
      setCoupons([]);
    } finally {
      setCouponsLoading(false);
    }
  };

  useEffect(() => {
    loadProgram();
  }, [programId]);

  useEffect(() => {
    if (program && program.type === 'coupon') {
      loadCoupons();
    }
  }, [program]);

  const handleEdit = () => {
    // Navigate to edit form or open edit modal
    navigate(`/marketing-programs/${programId}/edit`);
  };

  const handleDelete = async () => {
    if (!program) return;
    
    if (window.confirm(`Are you sure you want to delete "${program.name}"?`)) {
      try {
        await api.deleteMarketingProgram(program.id);
        navigate('/marketing-programs');
      } catch (error) {
        console.error('Error deleting marketing program:', error);
        alert('Failed to delete marketing program');
      }
    }
  };

  const handleGenerateCoupon = async (company: CompanyResource) => {
    if (!programId) return;
    
    try {
      await api.generateCoupon(programId, company.id);
      // Reload coupons to show the new one
      loadCoupons();
      alert(`Coupon generated successfully for ${company.companyName}`);
    } catch (error) {
      console.error('Error generating coupon:', error);
      alert('Failed to generate coupon');
    }
  };

  const handleGenerateEmptyCoupons = async (generateQty: number) => {
    if (!programId) return;
    
    try {
      await api.generateEmptyCoupons(programId, generateQty);
      // Reload coupons to show the new ones
      loadCoupons();
      alert(`${generateQty} empty coupons generated successfully`);
    } catch (error) {
      console.error('Error generating empty coupons:', error);
      throw error; // Re-throw to let the dialog handle the error
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('id-ID', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getDiscountDisplay = (type: string, value: number) => {
    if (type === 'percent') {
      return `${value}%`;
    }
    return formatCurrency(value);
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'coupon':
        return 'bg-blue-100 text-blue-800';
      case 'no-coupon':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'coupon':
        return 'Coupon';
      case 'no-coupon':
        return 'No Coupon';
      default:
        return type;
    }
  };

  const getStatusColor = (isActive: boolean) => {
    return isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800';
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/marketing-programs')}
            className="text-gray-500 hover:text-gray-700"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="h-8 w-64 bg-gray-200 rounded animate-pulse"></div>
        </div>
        <div className="bg-white shadow rounded-lg p-8">
          <LoadingSpinner size="md" text="Loading marketing program details..." />
        </div>
      </div>
    );
  }

  if (error || !program) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/marketing-programs')}
            className="text-gray-500 hover:text-gray-700"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Marketing Program Details</h1>
        </div>
        <div className="bg-white shadow rounded-lg p-8 text-center">
          <div className="text-red-600 mb-4">
            <XCircle className="mx-auto h-12 w-12 mb-4" />
            <h3 className="text-lg font-medium">Error Loading Program</h3>
            <p className="text-gray-500">{error || 'Marketing program not found'}</p>
          </div>
          <button
            onClick={() => navigate('/marketing-programs')}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Marketing Programs
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/marketing-programs')}
            className="text-gray-500 hover:text-gray-700"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{program.name}</h1>
            <p className="text-gray-600">Marketing Program Details</p>
          </div>
        </div>
        {canManage && (
          <div className="flex items-center space-x-3">
            <button
              onClick={handleEdit}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </button>
            <button
              onClick={handleDelete}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </button>
          </div>
        )}
      </div>

      {/* Program Details */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Information */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Program Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500">Name</label>
                <p className="mt-1 text-sm text-gray-900">{program.name}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Type</label>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTypeColor(program.type)}`}>
                  {getTypeLabel(program.type)}
                </span>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Status</label>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(program.isActive)}`}>
                  {program.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Minimum Month</label>
                <p className="mt-1 text-sm text-gray-900">{program.minimumMonth} month{program.minimumMonth > 1 ? 's' : ''}</p>
              </div>
              {program.description && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-500">Description</label>
                  <p className="mt-1 text-sm text-gray-900">{program.description}</p>
                </div>
              )}
            </div>
          </div>

          {/* Discount Information */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Discount Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500">Discount Type</label>
                <p className="mt-1 text-sm text-gray-900 capitalize">{program.discountType}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Discount Value</label>
                <p className="mt-1 text-sm text-gray-900 font-medium">
                  {getDiscountDisplay(program.discountType, program.discountValue)}
                </p>
              </div>
            </div>
          </div>

          {/* Usage Limits */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Usage Limits</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500">Total Usage Limit</label>
                <p className="mt-1 text-sm text-gray-900">
                  {program.usageLimit ? program.usageLimit.toLocaleString() : 'Unlimited'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Usage Limit Per User</label>
                <p className="mt-1 text-sm text-gray-900">
                  {program.usageLimitPerUser ? program.usageLimitPerUser : 'Unlimited'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Program Configuration */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Configuration</h2>
            <div className="space-y-4">
              {program.type === 'no-coupon' && program.programCode && (
                <div>
                  <label className="block text-sm font-medium text-gray-500">Program Code</label>
                  <p className="mt-1 text-sm text-gray-900 font-mono">{program.programCode}</p>
                </div>
              )}
              {program.type === 'coupon' && program.couponPrefix && (
                <div>
                  <label className="block text-sm font-medium text-gray-500">Coupon Prefix</label>
                  <p className="mt-1 text-sm text-gray-900 font-mono">{program.couponPrefix}</p>
                </div>
              )}
              {program.type === 'no-coupon' && program.startsAt && (
                <div>
                  <label className="block text-sm font-medium text-gray-500">Start Date</label>
                  <p className="mt-1 text-sm text-gray-900">{formatDate(program.startsAt)}</p>
                </div>
              )}
              {program.type === 'no-coupon' && program.endsAt && (
                <div>
                  <label className="block text-sm font-medium text-gray-500">End Date</label>
                  <p className="mt-1 text-sm text-gray-900">{formatDate(program.endsAt)}</p>
                </div>
              )}
              {program.type === 'coupon' && program.expiredDay && (
                <div>
                  <label className="block text-sm font-medium text-gray-500">Expiry Days</label>
                  <p className="mt-1 text-sm text-gray-900">{program.expiredDay} days</p>
                </div>
              )}
            </div>
          </div>

          {/* Program Image */}
          {program.image && (
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Program Image</h2>
              <img
                src={program.image}
                alt={program.name}
                className="w-full h-48 object-cover rounded-lg"
              />
            </div>
          )}

          {/* Timestamps */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Timestamps</h2>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-500">Created</label>
                <p className="mt-1 text-sm text-gray-900">{formatDate(program.createdAt)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Last Updated</label>
                <p className="mt-1 text-sm text-gray-900">{formatDate(program.updatedAt)}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Coupons List (only for coupon type) */}
      {program.type === 'coupon' && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-medium text-gray-900">Generated Coupons</h2>
              <div className="flex items-center space-x-4">
                {canManage && (
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setShowCompanySearch(true)}
                      className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                      <UserPlus className="h-3 w-3 mr-1" />
                      Generate Coupon
                    </button>
                    <button
                      onClick={() => setShowGenerateEmpty(true)}
                      className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Generate Empty
                    </button>
                  </div>
                )}
                <div className="flex items-center space-x-2">
                  <Tag className="h-5 w-5 text-gray-400" />
                  <span className="text-sm text-gray-500">{coupons.length} coupons</span>
                </div>
              </div>
            </div>
          </div>
          
          {couponsLoading ? (
            <div className="p-8">
              <LoadingSpinner size="md" text="Loading coupons..." />
            </div>
          ) : coupons.length === 0 ? (
            <div className="p-8 text-center">
              <Tag className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No coupons generated</h3>
              <p className="text-gray-500">This program hasn't generated any coupons yet.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Coupon Code
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Company
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Used At
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {coupons.map((coupon) => (
                    <tr key={coupon.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900 font-mono">
                          {coupon.couponCode}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {coupon.company ? (
                          <>
                            <div className="text-sm text-gray-900">{coupon.company.companyName}</div>
                            <div className="text-sm text-gray-500">{coupon.company.companyAdminEmail}</div>
                          </>
                        ) : (
                          <div className="text-sm text-gray-500 italic">No company assigned</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {coupon.usedAt ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Used
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            <Clock className="h-3 w-3 mr-1" />
                            Available
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {coupon.usedAt ? formatDate(coupon.usedAt) : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(coupon.createdAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Company Search Dialog */}
      <CompanySearchDialog
        isOpen={showCompanySearch}
        onClose={() => setShowCompanySearch(false)}
        onSelect={handleGenerateCoupon}
        title="Select Company for Coupon Generation"
      />

      {/* Generate Empty Coupons Dialog */}
      <GenerateEmptyCouponsDialog
        isOpen={showGenerateEmpty}
        onClose={() => setShowGenerateEmpty(false)}
        onGenerate={handleGenerateEmptyCoupons}
        programName={program?.name}
      />
    </div>
  );
};

export default MarketingProgramDetail;
